import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, TouchableOpacity } from 'react-native';
import { Text, Card, Button, ActivityIndicator, Divider, TextInput } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import api from '../../services/api';
import { generateOrdenBodegaHTML } from '../../utils/facturaGenerator';

const BodegaChecklistScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { pedido } = route.params;

  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState([]);
  const [ventaDetalle, setVentaDetalle] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [notasConfirmacion, setNotasConfirmacion] = useState('');

  // silent=true: refresca datos sin mostrar el spinner full-screen, así el
  // usuario no pierde el scroll cuando verifica un ítem.
  const loadChecklist = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [checklistData, ventaData] = await Promise.all([
        api.getBodegaChecklist(pedido.id),
        api.getVenta(pedido.id),
      ]);
      setChecklist(checklistData);
      setVentaDetalle(ventaData);
    } catch (error) {
      console.error('Error al cargar checklist:', error);
      if (!silent) Alert.alert('Error', 'No se pudo cargar el checklist');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pedido.id]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const handleToggleItem = async (item) => {
    // Optimista: flipear el checkbox al instante. Si el backend rechaza,
    // el siguiente loadChecklist devuelve el estado real.
    const nuevoVerificado = !item.verificado;
    setChecklist(prev => prev.map(it => it.id === item.id ? { ...it, verificado: nuevoVerificado } : it));
    try {
      if (item.verificado) {
        await api.desverificarItemBodega(item.id);
      } else {
        await api.verificarItemBodega(item.id, {
          cantidad_verificada: parseFloat(item.cantidad_solicitada),
        });
      }
      loadChecklist({ silent: true });
    } catch (error) {
      // Revertir el flip optimista y mostrar el error.
      setChecklist(prev => prev.map(it => it.id === item.id ? { ...it, verificado: item.verificado } : it));
      Alert.alert('Error', error.message);
    }
  };

  const handleImprimirOrden = async () => {
    try {
      const configRes = await api.getConfiguracion();

      // Si no hay logo en la DB, intentar usar el logo local
      if (!configRes.logo_url) {
        try {
          const logoPath = FileSystem.documentDirectory + 'logo.png';
          const logoExists = await FileSystem.getInfoAsync(logoPath);
          if (logoExists.exists) {
            const base64 = await FileSystem.readAsStringAsync(logoPath, {
              encoding: FileSystem.EncodingType.Base64,
            });
            configRes.logo_url = `data:image/png;base64,${base64}`;
          }
        } catch (logoError) {
          console.log('No se pudo cargar logo local:', logoError);
        }
      }

      const html = generateOrdenBodegaHTML({
        id: pedido.id,
        fecha: pedido.fecha,
        cliente_nombre: pedido.cliente_nombre,
        cliente_direccion: pedido.cliente_direccion,
        total: pedido.total,
        items: checklist.map(item => ({
          producto_nombre: item.producto_nombre,
          producto_codigo: item.producto_codigo,
          marca: item.marca,
          cantidad: item.cantidad_solicitada,
          unidad_nombre: item.unidad_nombre,
        })),
      }, configRes || {});
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri);
      }
    } catch (error) {
      Alert.alert('Error', 'No se pudo generar la orden: ' + error.message);
    }
  };

  const todosVerificados = checklist.length > 0 && checklist.every(item => item.verificado);
  const verificados = checklist.filter(item => item.verificado).length;
  const totalItems = checklist.length;
  const progreso = totalItems > 0 ? (verificados / totalItems) * 100 : 0;

  const handleConfirmar = () => {
    if (!todosVerificados) {
      Alert.alert('Checklist Incompleto', `Faltan ${totalItems - verificados} producto(s) por verificar`);
      return;
    }

    Alert.alert(
      'Confirmar Pedido',
      `¿Confirmar que el pedido #${pedido.id} está listo para entrega?`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Confirmar',
          onPress: async () => {
            try {
              setConfirming(true);
              await api.confirmarPedidoBodega(pedido.id, {
                notas: notasConfirmacion || 'Verificado y listo para entrega',
              });
              Alert.alert(
                'Pedido Confirmado',
                `Pedido #${pedido.id} marcado como preparado`,
                [{ text: 'OK', onPress: () => navigation.goBack() }]
              );
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setConfirming(false);
            }
          },
        },
      ]
    );
  };

  const handleVerificarTodos = () => {
    Alert.alert(
      'Verificar Todos',
      '¿Marcar todos los productos como verificados?',
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Verificar Todos',
          onPress: async () => {
            // Optimista: marcar todos como verificados de inmediato.
            setChecklist(prev => prev.map(it => ({ ...it, verificado: true })));
            try {
              for (const item of checklist) {
                if (!item.verificado) {
                  await api.verificarItemBodega(item.id, {
                    cantidad_verificada: parseFloat(item.cantidad_solicitada),
                  });
                }
              }
              loadChecklist({ silent: true });
            } catch (error) {
              loadChecklist({ silent: true });
              Alert.alert('Error', error.message);
            }
          },
        },
      ]
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#17A2B8" />
        <Text style={styles.loadingText}>Cargando checklist...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* Header del pedido */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.headerRow}>
            <View>
              <Text style={styles.pedidoId}>Pedido #{pedido.id}</Text>
              <Text style={styles.pedidoCliente}>
                <Ionicons name="person" size={14} color="#666" /> {pedido.cliente_nombre}
              </Text>
              {!!pedido.vendedor_nombre && (
                <Text style={styles.pedidoVendedor}>
                  <Ionicons name="business" size={14} color="#888" /> {pedido.vendedor_nombre}
                </Text>
              )}
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.pedidoTotal}>L. {parseFloat(pedido.total).toFixed(2)}</Text>
              <Text style={styles.estadoLabel}>
                {pedido.estado === 'pendiente' ? 'Por verificar' : 'Verificado'}
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* Barra de progreso */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.progressHeader}>
            <Text style={styles.progressTitle}>Progreso del Checklist</Text>
            <Text style={styles.progressCount}>{verificados}/{totalItems}</Text>
          </View>
          <View style={styles.progressBarBg}>
            <View style={[styles.progressBarFill, { width: `${progreso}%`, backgroundColor: todosVerificados ? '#28A745' : '#17A2B8' }]} />
          </View>
          {!todosVerificados && (
            <Button
              mode="text"
              onPress={handleVerificarTodos}
              style={styles.verificarTodosBtn}
              compact
            >
              Verificar Todos
            </Button>
          )}
        </Card.Content>
      </Card>

      {/* Botón imprimir orden */}
      <Button
        mode="outlined"
        icon="printer"
        onPress={handleImprimirOrden}
        style={{ marginBottom: 8, borderColor: '#17A2B8' }}
        textColor="#17A2B8"
        compact
      >
        Imprimir Orden de Preparacion
      </Button>

      {/* Lista de productos - Checklist */}
      <Card style={styles.card}>
        <Card.Title title="Productos a Verificar" />
        <Card.Content>
          {checklist.map((item, index) => (
            <View key={item.id}>
              <TouchableOpacity
                style={[styles.checklistItem, item.verificado && styles.checklistItemVerificado]}
                onPress={() => handleToggleItem(item)}
                disabled={pedido.estado !== 'pendiente'}
              >
                <View style={styles.checkboxArea}>
                  <Ionicons
                    name={item.verificado ? 'checkbox' : 'square-outline'}
                    size={28}
                    color={item.verificado ? '#28A745' : '#CCC'}
                  />
                </View>
                <View style={styles.itemInfo}>
                  <Text style={[styles.itemName, item.verificado && styles.itemNameVerificado]}>
                    {item.producto_nombre || 'Producto'}
                  </Text>
                  <View style={styles.itemDetails}>
                    {!!item.marca && (
                      <Text style={styles.itemMarca}>Marca: {item.marca}</Text>
                    )}
                    <Text style={styles.itemCodigo}>{item.producto_codigo}</Text>
                  </View>
                </View>
                <View style={styles.itemCantidad}>
                  <Text style={[styles.cantidadNumero, item.verificado && { color: '#28A745' }]}>
                    {parseFloat(item.cantidad_solicitada)}
                  </Text>
                  <Text style={styles.cantidadUnidad}>{item.unidad_nombre || 'ud'}</Text>
                  {!!item.verificado && !!item.verificado_en && (
                    <Ionicons name="checkmark-circle" size={16} color="#28A745" style={{ marginTop: 2 }} />
                  )}
                </View>
              </TouchableOpacity>
              {index < checklist.length - 1 && <Divider />}
            </View>
          ))}
        </Card.Content>
      </Card>

      {/* Notas y botón de confirmación */}
      {pedido.estado === 'pendiente' && (
        <Card style={styles.card}>
          <Card.Title title="Confirmar Pedido" />
          <Card.Content>
            <TextInput
              label="Notas de bodega (opcional)"
              value={notasConfirmacion}
              onChangeText={setNotasConfirmacion}
              style={styles.notasInput}
              multiline
              numberOfLines={2}
            />

            <Button
              mode="contained"
              icon={todosVerificados ? 'check-all' : 'alert-circle'}
              onPress={handleConfirmar}
              style={[styles.confirmarBtn, { backgroundColor: todosVerificados ? '#28A745' : '#999' }]}
              loading={confirming}
              disabled={confirming || !todosVerificados}
            >
              {todosVerificados
                ? 'Confirmar Pedido Listo'
                : `Faltan ${totalItems - verificados} por verificar`
              }
            </Button>
          </Card.Content>
        </Card>
      )}

      {pedido.estado !== 'pendiente' && (
        <Card style={[styles.card, { backgroundColor: '#D4EDDA' }]}>
          <Card.Content style={styles.confirmadoContainer}>
            <Ionicons name="checkmark-circle" size={40} color="#28A745" />
            <Text style={styles.confirmadoText}>Este pedido ya fue verificado y confirmado</Text>
          </Card.Content>
        </Card>
      )}

      <View style={{ height: 30 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 8 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: 10, color: '#666' },
  card: { marginBottom: 8, elevation: 2 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between' },
  pedidoId: { fontSize: 20, fontWeight: 'bold', color: '#333' },
  pedidoCliente: { fontSize: 14, color: '#666', marginTop: 4 },
  pedidoVendedor: { fontSize: 13, color: '#888', marginTop: 2 },
  headerRight: { alignItems: 'flex-end' },
  pedidoTotal: { fontSize: 18, fontWeight: 'bold', color: '#28a745' },
  estadoLabel: { fontSize: 12, color: '#888', marginTop: 2 },
  // Progress
  progressHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  progressTitle: { fontSize: 14, fontWeight: '600', color: '#333' },
  progressCount: { fontSize: 14, fontWeight: 'bold', color: '#17A2B8' },
  progressBarBg: { height: 8, backgroundColor: '#E9ECEF', borderRadius: 4, overflow: 'hidden' },
  progressBarFill: { height: '100%', borderRadius: 4 },
  verificarTodosBtn: { marginTop: 4, alignSelf: 'flex-end' },
  // Checklist items
  checklistItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 4 },
  checklistItemVerificado: { backgroundColor: '#F0FFF0' },
  checkboxArea: { marginRight: 12 },
  itemInfo: { flex: 1 },
  itemName: { fontSize: 15, fontWeight: '600', color: '#333' },
  itemNameVerificado: { textDecorationLine: 'line-through', color: '#888' },
  itemDetails: { flexDirection: 'row', marginTop: 2 },
  itemMarca: { fontSize: 12, color: '#17A2B8', marginRight: 8 },
  itemCodigo: { fontSize: 12, color: '#999' },
  itemCantidad: { alignItems: 'center', minWidth: 50 },
  cantidadNumero: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  cantidadUnidad: { fontSize: 11, color: '#888' },
  // Confirmación
  notasInput: { marginBottom: 12, backgroundColor: '#fff' },
  confirmarBtn: { marginTop: 4 },
  confirmadoContainer: { alignItems: 'center', padding: 16 },
  confirmadoText: { fontSize: 14, color: '#155724', marginTop: 8, textAlign: 'center' },
});

export default BodegaChecklistScreen;
